import { useMutation, useQueryClient } from "@tanstack/react-query";
import { queueApi } from "@bw/shared/api";
import type { QueueEntryResponse } from "@bw/shared/types/generated";
import { queryKeys } from "../../../common/query/queryClient";

export interface QueuePartyVariables {
  mode: string;
  /** ADR-025 Phase D party-lines (#64): queue into a SPECIFIC court's
   * self-serve line instead of the facility queue. Required at a self_serve
   * facility (a structured 409 SELF_SERVE_LINES_REQUIRED otherwise). */
  targetCourtId?: string;
}

/**
 * Queue the whole party for a court (POST /queue/party/{partyId}/queue) — a
 * facility-level entry, or a court LINE when `targetCourtId` is supplied
 * (ADR-025 Phase D party-lines, #64).
 *
 * Leader-only (server-enforced). On success the party transitions to queued
 * and the caller advances to the waiting view for the returned entry.
 * On settlement, invalidates `party.detail(partyId)` so rejected stale actions
 * reconcile as quickly as successful ones, and —
 * when a `facilityId` is known, the grouped court-board line projection and
 * current party/entry/mode state reconcile together. Errors are NOT swallowed — a 403 (not the
 * leader), 409 (a member is already queued, or the self_serve gate), or a
 * bad-mode ValueError surfaces on `mutation.error`.
 */
export function useQueueParty(partyId: string, facilityId?: string) {
  const queryClient = useQueryClient();

  return useMutation<QueueEntryResponse, unknown, QueuePartyVariables>({
    mutationFn: ({ mode, targetCourtId }) =>
      queueApi.queueParty(partyId, mode, targetCourtId),
    onSettled: () => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.party.detail(partyId),
        refetchType: "active",
      });
      if (facilityId) {
        void queryClient.invalidateQueries({
          queryKey: queryKeys.dropin.myParty(facilityId),
          refetchType: "active",
        });
        void queryClient.invalidateQueries({
          queryKey: queryKeys.dropin.partyMemberships(facilityId),
          refetchType: "active",
        });
        void queryClient.invalidateQueries({
          queryKey: queryKeys.dropin.courtBoard(facilityId),
          refetchType: "active",
        });
        void queryClient.invalidateQueries({
          queryKey: ["dropin", "my-entry", facilityId],
          refetchType: "active",
        });
        void queryClient.invalidateQueries({
          queryKey: queryKeys.dropin.session(facilityId),
          refetchType: "active",
        });
        void queryClient.invalidateQueries({
          queryKey: queryKeys.dropin.facility(facilityId),
          refetchType: "active",
        });
        void queryClient.invalidateQueries({
          queryKey: queryKeys.dropin.queueStats(facilityId),
          refetchType: "active",
        });
      }
    },
  });
}
